// Página completa de notificações. Lista cronológica com ações por item:
// marcar como lida/não-lida e ocultar. "Ocultar" não exclui — só some daqui
// (operador pode auditar depois). Visão dedicada via sidebar.
//
// Estado é GLOBAL (signal) pra compartilhar com:
//   - badge da sidebar (notifsState.unreadCount)
//   - card "Recentes" na home (notifsState.recent3Unread)
//
// load() é chamado no boot (App) + a cada 60s em background, e quando esta
// view é montada (re-fetch pra estar sempre fresca).

import { computed, signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  Bell,
  BellOff,
  Check,
  CheckCheck,
  Eye,
  EyeOff,
  Loader2,
  RefreshCw,
  Square,
  CheckSquare,
  X
} from "lucide-react";
import { api, ApiError, type NotificationRow } from "./api";
import { linkify, extractPayLink } from "./linkify";
import { openAlert } from "./ConfirmPromptModal";
import { setActiveNav, type NavKey } from "./App";
import {
  loadStudyGroups,
  openStudyGroup
} from "./StudyGroups";
import { call } from "./api";

interface NotifsState {
  phase: "idle" | "loading" | "ready" | "error";
  list: NotificationRow[];
  unreadCount: number;
  includeHidden: boolean;
  errMsg: string | null;
  lastLoadedAt: number;
}

export const notifsState = signal<NotifsState>({
  phase: "idle",
  list: [],
  unreadCount: 0,
  includeHidden: false,
  errMsg: null,
  lastLoadedAt: 0
});

/** As 3 últimas notifs NÃO-LIDAS pra mostrar destaque na home. */
export const recent3Unread = computed(() =>
  notifsState.value.list
    .filter((n) => !n.readAt && !n.hiddenAt)
    .slice(0, 3)
);

export async function loadNotifications(opts: { silent?: boolean } = {}) {
  if (!opts.silent) {
    notifsState.value = { ...notifsState.value, phase: "loading", errMsg: null };
  }
  try {
    const r = await api.listNotifications({
      includeHidden: notifsState.value.includeHidden
    });
    notifsState.value = {
      ...notifsState.value,
      phase: "ready",
      list: r.notifications,
      unreadCount: r.unreadCount,
      lastLoadedAt: Date.now()
    };
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    notifsState.value = {
      ...notifsState.value,
      phase: "error",
      errMsg: e instanceof Error ? e.message : String(e)
    };
  }
}

/** Mapeia um linkUrl interno (`/student/...`) pra NavKey + abre a seção via
 *  setActiveNav sem full reload. Retorna true se conseguiu rotear. Sem isso
 *  o `<a href>` recarregava a página e caía sempre em "home" porque o boot
 *  do StudentApp não parseia o path. Karol 2026-05-29: notificação de pedido
 *  de conexão "só recarregava a página". */
function handleInternalLinkClick(href: string): boolean {
  if (!href.startsWith("/student")) return false;
  try {
    const u = new URL(href, window.location.origin);
    const params = u.searchParams;
    const qNav = params.get("nav") || params.get("section") || params.get("view");
    const qNavMap: Record<string, NavKey> = {
      mensagens: "mensagens",
      notificacoes: "notificacoes",
      agenda: "agenda",
      disciplinas: "disciplinas",
      materias: "disciplinas",
      provas: "provas",
      bancos: "bancos",
      jogos: "jogos",
      gruposestudo: "gruposestudo",
      "grupos-estudo": "gruposestudo",
      compartilhamentos: "compartilhamentos",
      gravador: "gravador",
      conexoes: "conexoes",
      descobrir: "descobrir",
      anotacoes: "anotacoes",
      peerchat: "peerchat",
      aistudy: "aistudy",
      quickstudy: "quickstudy",
      "meus-reports": "meusreports",
      meusreports: "meusreports",
      minhaconta: "minhaconta",
      faq: "faq"
    };
    if (qNav && qNavMap[qNav]) {
      setActiveNav(qNavMap[qNav]);
      return true;
    }
    // Path segment: /student/<key>[/...]
    const seg = u.pathname.replace(/^\/student\/?/, "").split("/")[0];
    if (seg && qNavMap[seg]) {
      setActiveNav(qNavMap[seg]);
      return true;
    }
    // /student/banks/<id> → fallback pra acervo de bancos
    if (seg === "banks") {
      setActiveNav("bancos");
      return true;
    }
    if (seg === "scheduled-exams") {
      setActiveNav("provas");
      return true;
    }
    if (seg === "compras") {
      setActiveNav("disciplinas");
      return true;
    }
  } catch {
    /* fallthrough → deixa o <a> fazer o navigate padrão */
  }
  return false;
}

let _pollHandle: number | null = null;
/** Inicia polling a cada 60s. App chama no boot. Idempotente. */
export function startNotificationsPolling() {
  if (_pollHandle != null) return;
  void loadNotifications({ silent: true });
  _pollHandle = window.setInterval(() => {
    void loadNotifications({ silent: true });
  }, 60_000);
}

export async function setRead(id: string, read: boolean) {
  // Otimista
  notifsState.value = {
    ...notifsState.value,
    list: notifsState.value.list.map((n) =>
      n.id === id
        ? { ...n, readAt: read ? new Date().toISOString() : null }
        : n
    ),
    unreadCount: Math.max(
      0,
      notifsState.value.unreadCount + (read ? -1 : +1)
    )
  };
  try {
    if (read) await api.markNotifRead(id);
    else await api.markNotifUnread(id);
  } catch (e) {
    void openAlert({ title: "Erro", message: e instanceof Error ? e.message : String(e) });
    void loadNotifications({ silent: true });
  }
}

export async function hideNotif(id: string) {
  const target = notifsState.value.list.find((n) => n.id === id);
  const wasUnread = !!target && !target.readAt;
  notifsState.value = {
    ...notifsState.value,
    list: notifsState.value.list.filter((n) => n.id !== id),
    unreadCount: Math.max(0, notifsState.value.unreadCount + (wasUnread ? -1 : 0))
  };
  try {
    await api.hideNotif(id);
  } catch (e) {
    void openAlert({ title: "Erro", message: e instanceof Error ? e.message : String(e) });
    void loadNotifications({ silent: true });
  }
}

async function markAllRead() {
  notifsState.value = {
    ...notifsState.value,
    list: notifsState.value.list.map((n) =>
      n.readAt ? n : { ...n, readAt: new Date().toISOString() }
    ),
    unreadCount: 0
  };
  try {
    await api.markAllNotifsRead();
  } catch (e) {
    void openAlert({ title: "Erro", message: e instanceof Error ? e.message : String(e) });
    void loadNotifications({ silent: true });
  }
}

// ===== Ações em lote (atual: paraleliza chamadas single — bom para até ~50 itens) =====
async function bulkMarkRead(ids: string[]) {
  notifsState.value = {
    ...notifsState.value,
    list: notifsState.value.list.map((n) =>
      ids.includes(n.id) && !n.readAt
        ? { ...n, readAt: new Date().toISOString() }
        : n
    )
  };
  // Recalcula unreadCount otimista
  const newUnread = notifsState.value.list.filter(
    (n) => !n.readAt && !n.hiddenAt
  ).length;
  notifsState.value = { ...notifsState.value, unreadCount: newUnread };
  try {
    await Promise.all(ids.map((id) => api.markNotifRead(id).catch(() => null)));
  } catch (e) {
    void loadNotifications({ silent: true });
  }
}

async function bulkMarkUnread(ids: string[]) {
  notifsState.value = {
    ...notifsState.value,
    list: notifsState.value.list.map((n) =>
      ids.includes(n.id) ? { ...n, readAt: null } : n
    )
  };
  const newUnread = notifsState.value.list.filter(
    (n) => !n.readAt && !n.hiddenAt
  ).length;
  notifsState.value = { ...notifsState.value, unreadCount: newUnread };
  try {
    await Promise.all(ids.map((id) => api.markNotifUnread(id).catch(() => null)));
  } catch (e) {
    void loadNotifications({ silent: true });
  }
}

async function bulkHide(ids: string[]) {
  const set = new Set(ids);
  notifsState.value = {
    ...notifsState.value,
    list: notifsState.value.list.filter((n) => !set.has(n.id))
  };
  const newUnread = notifsState.value.list.filter(
    (n) => !n.readAt && !n.hiddenAt
  ).length;
  notifsState.value = { ...notifsState.value, unreadCount: newUnread };
  try {
    await Promise.all(ids.map((id) => api.hideNotif(id).catch(() => null)));
  } catch (e) {
    void loadNotifications({ silent: true });
  }
}

export function NotificationsView({ compact = false }: { compact?: boolean } = {}) {
  useEffect(() => {
    void loadNotifications();
  }, []);

  const state = notifsState.value;
  const showHidden = state.includeHidden;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openItem, setOpenItem] = useState<NotificationRow | null>(null);
  const selCount = selected.size;
  const inSelectionMode = selCount > 0;

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }
  function selectAll() {
    setSelected(new Set(state.list.map((n) => n.id)));
  }
  function clearSelection() {
    setSelected(new Set());
  }
  /** Mapeia linkUrl de notificação pra NavKey do student-new. */
  function navKeyFromLinkUrl(link: string, title: string): NavKey | null {
    if (!link) return null;
    const path = link.replace(/^https?:\/\/[^/]+/, "");
    const m = /^\/student\/([a-z]+)/.exec(path);
    const seg = m?.[1] ?? "";
    const map: Record<string, NavKey> = {
      conexoes: "conexoes",
      mensagens: "mensagens",
      peerchat: "peerchat",
      notificacoes: "notificacoes",
      agenda: "agenda",
      bancos: "bancos",
      disciplinas: "disciplinas",
      provas: "provas",
      assinatura: "assinatura",
      formapagamento: "formapagamento",
      gravador: "gravador",
      anotacoes: "anotacoes",
      compartilhamentos: "compartilhamentos",
      descobrir: "descobrir",
      jogos: "jogos",
      aistudy: "aistudy"
    };
    if (seg && map[seg]) return map[seg];
    // Fallback heurístico via título (pra notificações antigas sem linkUrl).
    if (/conex/i.test(title)) return "conexoes";
    if (/mensag/i.test(title)) return "mensagens";
    return null;
  }

  function openModal(n: NotificationRow) {
    // Marca como lida automaticamente ao abrir
    if (!n.readAt) void setRead(n.id, true);

    // Deeplink: navega via setActiveNav (mecanismo interno do student-new)
    // em vez de só pushState — porque o App não escuta popstate, depende
    // do signal `activeNav` pra trocar de view. Casos cobertos:
    //   - Pedido de conexão (linkUrl=/student/conexoes + título contém
    //     "conex") → vai pra Conexões + ?tab=pending-in no URL.
    //   - Outras URLs /student/<key> → mapeia pra NavKey conhecida.
    //   - Resto cai no fallback de abrir o modal.
    const link = (n.linkUrl ?? "").trim();
    if (link) {
      const navKey = navKeyFromLinkUrl(link, n.title ?? "");
      if (navKey) {
        // Mantém query string original se houver — usado pelo destino pra
        // contextualizar (ex: ConexoesView lê ?tab=pending-in).
        let qs = "";
        const qIdx = link.indexOf("?");
        if (qIdx >= 0) qs = link.slice(qIdx);
        else if (navKey === "conexoes" && /conex/i.test(n.title ?? "")) {
          qs = "?tab=pending-in";
        }
        const target = `/student/${navKey}${qs}`;
        // pushState pra refletir no URL (caso user copie/recarregue) e
        // navega via signal (que é o que troca a view de fato).
        try {
          window.history.pushState({}, "", target);
        } catch {
          /* ignore */
        }
        setActiveNav(navKey);
        return;
      }
    }
    // Fallback: abre modal pra notificações sem linkUrl ou que não casam.
    setOpenItem(n);
  }
  async function bulkAction(kind: "read" | "unread" | "hide") {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (kind === "read") await bulkMarkRead(ids);
    else if (kind === "unread") await bulkMarkUnread(ids);
    else await bulkHide(ids);
    clearSelection();
  }

  return (
    <div class="space-y-5">
      {/* Header normal ou modo seleção. Em `compact` (embarcado no
          InboxCenterView), o header próprio é suprimido — quem hospeda já
          tem o seu. A barra de seleção em lote continua aparecendo. */}
      {inSelectionMode ? (
        <div class="sticky top-0 z-10 -mx-5 flex flex-wrap items-center justify-between gap-2 border-y border-primary/30 bg-primary/10 px-5 py-3 backdrop-blur-md md:-mx-8 md:px-8">
          <div class="flex items-center gap-3">
            <button
              type="button"
              onClick={clearSelection}
              title="Limpar seleção"
              aria-label="Limpar seleção"
              class="grid size-8 place-items-center rounded-md border border-border bg-background hover:bg-accent"
            >
              <X className="size-4" />
            </button>
            <span class="text-sm font-bold">
              {selCount} selecionada{selCount === 1 ? "" : "s"}
            </span>
            <button
              type="button"
              onClick={selectAll}
              class="text-xs font-semibold text-primary hover:underline"
            >
              Selecionar tudo ({state.list.length})
            </button>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => bulkAction("read")}
              class="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/15 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/25"
            >
              <Check className="size-3.5" /> Marcar lidas
            </button>
            <button
              type="button"
              onClick={() => bulkAction("unread")}
              class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
            >
              <Eye className="size-3.5" /> Não-lidas
            </button>
            <button
              type="button"
              onClick={() => bulkAction("hide")}
              class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
            >
              <BellOff className="size-3.5" /> Ocultar
            </button>
          </div>
        </div>
      ) : compact ? null : (
        <header class="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
              <Bell className="size-6 text-primary" />
              Notificações
            </h1>
            <p class="mt-1 text-sm text-muted-foreground">
              Clique para abrir; ou selecione várias pra agir em lote.
            </p>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            {state.list.length > 0 && (
              <button
                type="button"
                onClick={selectAll}
                class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
              >
                <CheckSquare className="size-3.5" />
                Selecionar tudo
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                notifsState.value = { ...state, includeHidden: !showHidden };
                void loadNotifications();
              }}
              class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
            >
              {showHidden ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
              {showHidden ? "Sem ocultas" : "Incluir ocultas"}
            </button>
            {state.unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                class="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/20"
              >
                <CheckCheck className="size-3.5" />
                Marcar todas lidas
              </button>
            )}
            <button
              type="button"
              onClick={() => void loadNotifications()}
              title="Atualizar"
              aria-label="Atualizar"
              class="grid size-8 place-items-center rounded-md border border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <RefreshCw className="size-3.5" />
            </button>
          </div>
        </header>
      )}

      {state.phase === "loading" && state.list.length === 0 && (
        <p class="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Carregando…
        </p>
      )}

      {state.phase === "error" && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 p-4">
          <p class="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="size-4" />
            {state.errMsg}
          </p>
        </div>
      )}

      {state.phase === "ready" && state.list.length === 0 && (
        <div class="rounded-md border border-dashed border-border bg-card/40 p-10 text-center">
          <BellOff className="mx-auto mb-3 size-8 text-muted-foreground/60" />
          <p class="text-sm text-muted-foreground">
            Nenhuma notificação {showHidden ? "" : "ativa"} no momento.
          </p>
        </div>
      )}

      <ul class="space-y-2">
        {state.list.map((n) => (
          <NotificationItem
            key={n.id}
            n={n}
            selected={selected.has(n.id)}
            onToggle={() => toggle(n.id)}
            onOpen={() => openModal(n)}
            onRead={() => setRead(n.id, true)}
            onUnread={() => setRead(n.id, false)}
            onHide={() => hideNotif(n.id)}
          />
        ))}
      </ul>

      {openItem && (
        <NotificationModal
          n={openItem}
          onClose={() => setOpenItem(null)}
          onHide={() => {
            void hideNotif(openItem.id);
            setOpenItem(null);
          }}
          onUnread={() => {
            void setRead(openItem.id, false);
            setOpenItem(null);
          }}
        />
      )}
    </div>
  );
}

function NotificationItem({
  n,
  selected,
  onToggle,
  onOpen,
  onRead,
  onUnread,
  onHide
}: {
  n: NotificationRow;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onRead: () => void;
  onUnread: () => void;
  onHide: () => void;
}) {
  const isUnread = !n.readAt;
  const isHidden = !!n.hiddenAt;
  return (
    <li
      class={`group rounded-md border px-4 py-3 transition-colors cursor-pointer ${
        selected
          ? "border-primary bg-primary/10 ring-1 ring-primary/30"
          : isHidden
          ? "border-border bg-muted/30 opacity-60"
          : isUnread
          ? "border-primary/40 bg-primary/5 hover:bg-primary/10"
          : "border-border bg-card/60 hover:bg-accent/30"
      }`}
      onClick={onOpen}
    >
      <div class="flex flex-wrap items-start gap-3">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          title={selected ? "Desmarcar" : "Selecionar"}
          aria-label={selected ? "Desmarcar" : "Selecionar"}
          class={`mt-0.5 grid size-5 shrink-0 place-items-center rounded border-2 transition-colors ${
            selected
              ? "border-primary bg-primary text-white"
              : "border-border bg-background text-transparent hover:border-primary"
          }`}
        >
          <Check className="size-3.5" strokeWidth={3} />
        </button>
        <NotifKindIcon kind={n.kind} />
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-center gap-2">
            <span class="text-sm font-bold">{n.title}</span>
            {isUnread && !isHidden && (
              <span class="rounded bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground">
                NOVA
              </span>
            )}
            <span class="text-[11px] text-muted-foreground">
              {formatRelative(n.createdAt)}
            </span>
          </div>
          {n.body && (
            <p class="mt-1 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
              {n.body}
            </p>
          )}
          {n.kind === "study_group_invite" && (
            <StudyGroupInviteInlineActions notif={n} onResolved={onHide} />
          )}
        </div>
        <div class="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (isUnread) onRead();
              else onUnread();
            }}
            title={isUnread ? "Marcar como lida" : "Marcar como não-lida"}
            aria-label={isUnread ? "Marcar como lida" : "Marcar como não-lida"}
            class="grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <Check className={`size-3.5 ${isUnread ? "" : "opacity-30"}`} />
          </button>
          {!isHidden && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onHide();
              }}
              title="Ocultar"
              aria-label="Ocultar"
              class="grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <BellOff className="size-3.5" />
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

export function NotificationModal({
  n,
  onClose,
  onHide,
  onUnread
}: {
  n: NotificationRow;
  onClose: () => void;
  onHide: () => void;
  onUnread: () => void;
}) {
  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-background/80 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        class="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div class="flex items-start gap-3 min-w-0">
            <NotifKindIcon kind={n.kind} />
            <div class="min-w-0">
              <h2 class="text-base font-bold">{n.title}</h2>
              <p class="mt-0.5 text-xs text-muted-foreground">
                {new Date(n.createdAt).toLocaleString("pt-BR")}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>
        <div class="flex-1 overflow-y-auto px-5 py-4">
          {(() => {
            const payLink =
              extractPayLink(n.body) ||
              (n.linkUrl && /\/pay\//.test(n.linkUrl) ? n.linkUrl : null);
            return (
              <>
                {payLink && (
                  <a
                    href={payLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="mb-3 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-bold text-primary-foreground hover:bg-primary/90"
                  >
                    Abrir link de pagamento →
                  </a>
                )}
                {n.body ? (
                  <p class="whitespace-pre-wrap text-sm leading-relaxed">
                    {linkify(n.body)}
                  </p>
                ) : (
                  <p class="text-sm italic text-muted-foreground">
                    Esta notificação não tem corpo detalhado.
                  </p>
                )}
                {n.linkUrl && !payLink && (
                  <a
                    href={n.linkUrl}
                    onClick={(e) => {
                      const handled = handleInternalLinkClick(n.linkUrl!);
                      if (handled) {
                        e.preventDefault();
                        onClose();
                      }
                    }}
                    class="mt-4 inline-block text-sm font-semibold text-primary hover:underline"
                  >
                    Ir para o conteúdo relacionado →
                  </a>
                )}
              </>
            );
          })()}
        </div>
        <footer class="flex flex-wrap items-center justify-end gap-2 border-t border-border bg-muted/20 px-5 py-3">
          <button
            type="button"
            onClick={onUnread}
            class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
          >
            <Eye className="size-3.5" /> Marcar como não-lida
          </button>
          <button
            type="button"
            onClick={onHide}
            class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
          >
            <BellOff className="size-3.5" /> Ocultar
          </button>
          <button
            type="button"
            onClick={onClose}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
          >
            Fechar
          </button>
        </footer>
      </div>
    </div>
  );
}

function NotifKindIcon({ kind }: { kind: NotificationRow["kind"] }) {
  // Cor por categoria pra escaneamento rápido.
  let bg = "bg-muted text-muted-foreground";
  let letter = "•";
  if (kind === "payment_received" || kind === "payment_pending") {
    bg = "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400";
    letter = "$";
  } else if (kind === "lesson_scheduled" || kind === "lesson_reminder") {
    bg = "bg-blue-500/15 text-blue-600 dark:text-blue-400";
    letter = "📅";
  } else if (kind === "lesson_logged") {
    bg = "bg-violet-500/15 text-violet-600 dark:text-violet-400";
    letter = "✓";
  } else if (kind === "lesson_canceled") {
    bg = "bg-red-500/15 text-red-600 dark:text-red-400";
    letter = "✕";
  } else if (kind === "credit_low" || kind === "credit_zero") {
    bg = "bg-amber-500/15 text-amber-600 dark:text-amber-400";
    letter = "!";
  } else if (kind === "enrolled_in_offering" || kind === "assigned_to_disciplina") {
    bg = "bg-primary/10 text-primary";
    letter = "+";
  }
  return (
    <span
      class={`grid size-8 shrink-0 place-items-center rounded-md text-sm font-bold ${bg}`}
      aria-hidden="true"
    >
      {letter}
    </span>
  );
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (diff < 60_000) return "agora";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)} min`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)} h`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)} d`;
  return new Date(iso).toLocaleDateString("pt-BR");
}

/** Aceitar/Recusar inline em notif de convite de Grupo de Estudo.
 *  Não ocupa muito espaço — fica logo abaixo do body. Cliques param a
 *  propagação pra não abrir o modal de detalhe. */
function StudyGroupInviteInlineActions({
  notif,
  onResolved
}: {
  notif: NotificationRow;
  onResolved: () => void;
}) {
  const [busy, setBusy] = useState<"accept" | "decline" | null>(null);
  const [done, setDone] = useState<"accepted" | "declined" | null>(null);
  const payload = (notif.payload || {}) as { inviteId?: string; groupId?: string };
  const inviteId = payload.inviteId;
  if (!inviteId) return null;
  if (done) {
    return (
      <div class="mt-2 text-xs text-muted-foreground">
        {done === "accepted"
          ? "Convite aceito. Você já é membro."
          : "Convite recusado."}
      </div>
    );
  }
  async function respond(action: "accept" | "decline", e: MouseEvent) {
    e.stopPropagation();
    setBusy(action);
    try {
      const res = await call<{ ok: boolean; groupId?: string }>(
        `/api/student/study-group-invites?id=${encodeURIComponent(inviteId!)}&action=${action}`,
        { method: "PATCH" }
      );
      setDone(action === "accept" ? "accepted" : "declined");
      void loadStudyGroups({ silent: true });
      // Oculta a notif e abre o grupo, no caso de aceite.
      if (action === "accept" && res.groupId) {
        setTimeout(() => {
          setActiveNav("gruposestudo" as NavKey);
          openStudyGroup(res.groupId!);
          onResolved();
        }, 400);
      } else {
        setTimeout(onResolved, 400);
      }
    } catch (err) {
      void openAlert({
        title: "Não consegui responder",
        message: err instanceof Error ? err.message : String(err)
      });
    } finally {
      setBusy(null);
    }
  }
  return (
    <div class="mt-2 flex gap-2" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        title="Aceitar convite"
        aria-label="Aceitar convite"
        disabled={busy !== null}
        onClick={(e) => void respond("accept", e)}
        class="inline-flex items-center gap-1 rounded-md bg-violet-600 px-2 py-1 text-xs font-medium text-white transition hover:bg-violet-700 disabled:opacity-60"
      >
        {busy === "accept" ? (
          <Loader2 className="size-3 animate-spin" />
        ) : (
          <Check className="size-3" />
        )}
        Aceitar
      </button>
      <button
        type="button"
        title="Recusar convite"
        aria-label="Recusar convite"
        disabled={busy !== null}
        onClick={(e) => void respond("decline", e)}
        class="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-xs font-medium text-muted-foreground transition hover:text-foreground disabled:opacity-60"
      >
        {busy === "decline" ? (
          <Loader2 className="size-3 animate-spin" />
        ) : (
          <X className="size-3" />
        )}
        Recusar
      </button>
    </div>
  );
}
